import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function Purchase({ searchParams }) {
  const { api, navigate } = useAuth();

  // ===== TOGGLE THIS TO PAUSE/UNPAUSE PURCHASES =====
  const PURCHASES_PAUSED = false;
  // ===================================================

  const [selectedLeads, setSelectedLeads] = useState(5);
  const [leadsPerWeek, setLeadsPerWeek] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  if (PURCHASES_PAUSED) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center">
          <div className="w-20 h-20 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <svg className="w-10 h-10 text-navy-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="font-display text-3xl text-navy-900 mb-4">Purchases Temporarily Paused</h1>
          <p className="text-navy-600 text-lg font-body mb-4">
            We're currently focused on delivering leads to our existing clients and have temporarily paused new purchases for the season.
          </p>
          <p className="text-navy-600 font-body mb-8">
            We'll let everyone know as soon as purchases are open again. In the meantime, your existing leads will continue to be delivered as normal.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary px-8 py-3"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const paymentStatus = searchParams?.get('payment');

  // Tiered pricing
  const getPricePerLead = (leadCount) => {
    if (leadCount >= 10) return 80;
    if (leadCount >= 4) return 85;
    return 90;
  };

  // Calculate delivery weeks based on leads and leads per week
  const deliveryWeeks = Math.ceil(selectedLeads / leadsPerWeek);
  
  // Warning for high volume
  const showWarning = leadsPerWeek >= 8;

  const pricePerLead = getPricePerLead(selectedLeads);
  const totalPrice = selectedLeads * pricePerLead;

  const handlePurchase = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const data = await api('/stripe/create-checkout', {
        method: 'POST',
        body: JSON.stringify({
          packageSize: selectedLeads,
          leadsPerWeek: leadsPerWeek
        })
      });

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      setError(error.message);
      setIsProcessing(false);
    }
  };

  // For development/testing - simulate payment
  const handleSimulatePayment = async () => {
    setIsProcessing(true);
    try {
      await api('/stripe/simulate-payment', {
        method: 'POST',
        body: JSON.stringify({
          packageSize: selectedLeads,
          leadsPerWeek: leadsPerWeek
        })
      });
      navigate('/dashboard?payment=success');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Payment Status Messages */}
      {paymentStatus === 'cancelled' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-amber-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-amber-800 font-body">Payment was cancelled. You can try again below.</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="font-display text-4xl text-navy-900 mb-4">Purchase Lead Package</h1>
        <p className="text-navy-600 text-lg max-w-2xl mx-auto font-body">
          Choose how many leads you'd like and how quickly you want to receive them.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Package Selection */}
        <div className="card-elevated">
          <h2 className="font-display text-xl text-navy-900 mb-6">Customize Your Package</h2>
          
          {/* Total Leads Slider */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-navy-900">Total Leads</h3>
              <span className="text-2xl font-display text-highlight-600">{selectedLeads}</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={selectedLeads}
              onChange={(e) => setSelectedLeads(parseInt(e.target.value))}
              className="w-full h-2 bg-cream-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between mt-2 text-sm text-navy-500 font-body">
              <span>1</span>
              <span>10</span>
              <span>20</span>
            </div>
            
            {/* Pricing indicator */}
            <div className="mt-4 text-center">
              <span className="text-sm text-navy-600 font-body">
                ${pricePerLead} per lead
                {selectedLeads >= 4 && selectedLeads < 10 && <span className="text-highlight-600 ml-2">💰 Volume discount!</span>}
                {selectedLeads >= 10 && <span className="text-highlight-600 ml-2">💰 Best price!</span>}
              </span>
            </div>
          </div>

          {/* Leads Per Week Slider */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-navy-900">Leads Per Week</h3>
              <span className="text-2xl font-display text-highlight-600">{leadsPerWeek}</span>
            </div>
            <p className="text-sm text-navy-600 mb-4 font-body">
              Choose how many leads you want to receive each week. 
              More leads per week means faster delivery.
            </p>

            <div className="mb-6">
              <input
                type="range"
                min="1"
                max="20"
                value={leadsPerWeek}
                onChange={(e) => setLeadsPerWeek(parseInt(e.target.value))}
                className="w-full h-2 bg-cream-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between mt-2 text-sm text-navy-500 font-body">
                <span>1/week</span>
                <span>10/week</span>
                <span>20/week</span>
              </div>
            </div>

            {/* Delivery timeline info */}
            <div className="bg-cream-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-navy-600 font-body">Your leads will be delivered over:</span>
                <span className="font-medium text-navy-900">{deliveryWeeks} week{deliveryWeeks !== 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* High volume warning */}
            {showWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-amber-800 font-body">
                    <strong>Whoa!</strong> {leadsPerWeek} leads per week is a lot. I don't want to see you wasting money if you can't follow up with them all. Are you sure you want that many per week?
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div>
          <div className="card-elevated bg-navy-900 text-white">
            <h2 className="font-display text-xl mb-6">Order Summary</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <span className="text-navy-300 font-body">Total leads</span>
                <span className="font-medium">{selectedLeads}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy-300 font-body">Price per lead</span>
                <span className="font-medium">${pricePerLead}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy-300 font-body">Leads per week</span>
                <span className="font-medium">{leadsPerWeek}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy-300 font-body">Delivery timeline</span>
                <span className="font-medium">{deliveryWeeks} week{deliveryWeeks !== 1 ? 's' : ''}</span>
              </div>
              <div className="border-t border-navy-700 pt-4">
                <div className="flex justify-between text-lg">
                  <span className="font-body">Total</span>
                  <span className="font-display text-2xl text-white">
                    ${totalPrice}
                  </span>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-500/50 text-red-200 px-4 py-3 rounded mb-4 text-sm font-body">
                {error}
              </div>
            )}

            <button
              onClick={handlePurchase}
              disabled={isProcessing}
              className="w-full py-4 font-medium rounded transition-colors bg-coral-500 hover:bg-coral-600 text-white disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Proceed to Payment'}
            </button>

            {/* Development: Simulate Payment */}
            {process.env.NODE_ENV !== 'production' && (
              <button
                onClick={handleSimulatePayment}
                disabled={isProcessing}
                className="w-full mt-3 py-2 bg-transparent border border-navy-600 text-navy-300 text-sm rounded hover:bg-navy-800 transition-colors font-body disabled:opacity-50"
              >
                [Dev] Simulate Payment
              </button>
            )}

            <p className="text-center text-navy-400 text-xs mt-4 font-body">
              Secure payment powered by Stripe
            </p>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-cream-50 rounded-lg">
            <h3 className="font-medium text-navy-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-highlight-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Volume Pricing
            </h3>
            <p className="text-sm text-navy-600 font-body">
              Save more when you buy more leads: $90 each for 1-3 leads, $85 each for 4-9 leads, and $80 each for 10+ leads.
            </p>
          </div>

          {/* Flexibility Note */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-medium text-navy-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              You Can Adjust Later
            </h3>
            <p className="text-sm text-navy-600 font-body">
              You can change your leads-per-week setting anytime from your dashboard if your capacity changes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
